import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Label, Input, Checkbox } from "semantic-ui-react";
import { Virtuoso } from "react-virtuoso";
import { ButtonIcon } from "../../../util"; // Assuming this utility is available

type OptionType = {
  key: string | number;
  text: string;
  value: string;
};

interface MultiSelectDropdownProps {
  options: OptionType[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding-right: 12px !important;
`;

const DropdownText = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "placeholder",
})<{ placeholder?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownMenu = styled.div`
  position: absolute;
  width: 100%;
  margin-top: 4px;
  z-index: 1000;
  animation: fade-in 0.15s;
  background: ${({ theme }) => theme.colors["container-background"]};
  border: 1px solid
    ${({ theme }) => theme.colors["container-secondary-border-color"]};
  border-radius: 4px;

  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const SearchWrapper = styled.div`
  padding: 0.5em;
  border-bottom: 1px solid rgba(34, 36, 38, 0.1);
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.78571429em 1.14285714em;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
    background-color: ${({ theme }) =>
      theme.colors["container-secondary-border-color"]};
  }
`;

const OptionLabel = styled.div`
  margin-left: 0.5em;
`;

const CountLabel = styled(Label)`
  position: absolute !important;
  right: -8px !important;
  top: -8px !important;
`;

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select options",
  style = {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [hasOpenedBefore, setHasOpenedBefore] = useState(false);

  const selectedCount = value.length;
  const NO_HIGHLIGHT = -1;

  // Filter options based on search term
  const filteredOptions = options
    .map((opt) => ({
      ...opt,
      text: String(opt.text),
    }))
    .filter((option) =>
      option.text.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (filteredOptions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredOptions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredOptions[highlightedIndex]) {
        const selectedOption = filteredOptions[highlightedIndex];
        handleOptionSelect(selectedOption.value);
      }
    }
  };

  // Close dropdown when clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(0);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Toggle option selection
  const handleOptionSelect = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
    setHighlightedIndex(NO_HIGHLIGHT);
  };

  // Handle search change and reset highlighted index
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setHighlightedIndex(0); // Reset to first option
  };

  // Create summary text for the dropdown header
  const getSummaryText = () => {
    if (value.length === 0) return placeholder;

    const selectedOptions = options.filter((opt) => value.includes(opt.value));
    if (selectedOptions.length === 0) return placeholder;

    if (selectedOptions.length === 1) return selectedOptions[0].text;

    return `${selectedOptions[0].text} +${selectedOptions.length - 1} more`;
  };

  return (
    <DropdownWrapper ref={dropdownRef} style={style}>
      <DropdownHeader
        onClick={() => {
          setIsOpen(!isOpen);
          if (!hasOpenedBefore) {
            setHighlightedIndex(0);
            setHasOpenedBefore(true);
          } else {
            setHighlightedIndex(NO_HIGHLIGHT);
          }
        }}
        className={`ui fluid search selection dropdown ${isOpen ? "visible active" : ""}`}
        style={{
          ...style,
          width: "100%",
        }}
      >
        <DropdownText placeholder={value.length === 0}>
          {getSummaryText()}
        </DropdownText>
        <ButtonIcon
          name={isOpen ? "angle up" : "angle down"}
          size="small"
          style={{ margin: 0 }}
        />
      </DropdownHeader>

      {selectedCount > 0 && (
        <CountLabel circular color="blue" size="mini">
          {selectedCount}
        </CountLabel>
      )}

      {isOpen && (
        <DropdownMenu className="menu visible transition">
          <SearchWrapper>
            <Input
              fluid
              icon="search"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              style={{
                ...style,
                width: "100%",
              }}
            />
          </SearchWrapper>

          {filteredOptions.length > 0 ? (
            <Virtuoso
              style={{
                height:
                  filteredOptions.length > 6
                    ? "240px"
                    : `${filteredOptions.length * 40}px`,
              }}
              totalCount={filteredOptions.length}
              itemContent={(index) => {
                const option = filteredOptions[index];
                return (
                  <DropdownItem
                    key={option.key}
                    className={value.includes(option.value) ? "active" : ""}
                    onClick={() => handleOptionSelect(option.value)}
                    style={{
                      backgroundColor:
                        index === highlightedIndex
                          ? "rgba(0, 123, 255, 0.1)"
                          : "transparent",
                      borderLeft:
                        index === highlightedIndex
                          ? "3px solid rgba(0, 123, 255, 0.6)"
                          : "3px solid transparent",
                    }}
                  >
                    <Checkbox checked={value.includes(option.value)} readOnly />
                    <OptionLabel>{option.text}</OptionLabel>
                  </DropdownItem>
                );
              }}
            />
          ) : (
            <DropdownItem style={{ cursor: "default" }}>
              No matching options found
            </DropdownItem>
          )}
        </DropdownMenu>
      )}
    </DropdownWrapper>
  );
};

export default MultiSelectDropdown;
